import type { Schemas } from '#shopware'
import { type Ref, ref } from 'vue'
import { useNuxtApp } from '#imports'
import { useIntersectionObserver } from '@vueuse/core'

export function useItemListObserver(
    targetElementRef: Ref<HTMLElement | null>,
    itemListId: string,
    itemListName: string,
    products: Schemas['Product'][],
    page?: number,
    limit?: number
) {
    const nuxtApp = useNuxtApp()
    const hasTracked = ref(false) // Prevent duplicate tracking

    const viewItemList = (): void => {
        if (hasTracked.value) return

        // Add absolute indices if page and limit are provided
        let productsToEmit = products
        if (page != null && limit != null) {
            const baseIndex = (page - 1) * limit
            productsToEmit = products.map((product, index) => ({
                ...product,
                absoluteIndex: baseIndex + index
            }))
        }

        nuxtApp.callHook('viewProductListing', {
            id: itemListId,
            name: itemListName,
            products: productsToEmit
        })

        hasTracked.value = true
    }

    // Use VueUse intersection observer
    // Automatically cleans up on component unmount via tryOnScopeDispose
    const { stop } = useIntersectionObserver(
        targetElementRef,
        (entries) => {
            const entry = entries[0]
            if (entry?.isIntersecting && products?.length > 0) {
                viewItemList()
                stop() // Stop observing after first trigger
            }
        },
        {
            threshold: 0.1 // Fire when 10% visible
        }
    )

    return {
        viewItemList
    }
}
